import React, { useState, useMemo } from 'react'

import DropDownMenu from '@/common/components/DropDownMenu'
import DropDownRange from '@/common/components/DropDownRange'
import Gap from '@/common/components/Gap'
import Image from '@/common/components/Image'
import Pagination from '@/common/components/Pagination'
import Table, { TableProps } from '@/common/components/Table'
import useI18n from '@/common/hooks/useI18n'
import { SEE_DETAILS } from '@/common/translation'
import { getPageData } from '@/common/utils/array'
import { numberToString } from '@/common/utils/string'
import { resolvePath, getFileUrl } from '@/common/utils/url'
import {
  ROOM_STATUS,
  ROOM_TYPE,
  ROOM_SIZE,
  FLOOR,
  RENTAL_PRICE_WITH_UNIT,
  FURNITURE,
} from '@/modules/property/pages/PlaceDetailPage/translation'
import { ROOM_DETAIL_PATH } from '@/modules/property/paths'
import { N_BEDROOMS } from '@/modules/property/translation'
import getFurnitureText from '@/modules/property/utils/getFurnitureText'

import {
  PAGE_SIZE,
  SIZE_MIN,
  SIZE_MAX,
  PRICE_MIN,
  PRICE_MAX,
  PRICE_STEP,
  ROOM_STATUS_OPTIONS,
  ROOM_TYPE_OPTIONS,
  FLOOR_OPTIONS,
  FURNITURE_OPTIONS,
} from './constants'
import { DotStatus, RoomDetailsButton } from './styled'

interface UnitListTableProps
  extends Omit<TableProps<RentAPerfect.Room>, 'columns'> {
  filter: {
    status?: string
    bedroom?: number
    size: [number, number]
    floor?: string
    price: [number, number]
    furniture?: string
  }
  onFilterChange: (key: string) => (value: any) => void
}

const UnitListTable = ({
  data,
  filter,
  onFilterChange,
  ...props
}: UnitListTableProps) => {
  const { t } = useI18n()
  const [page, setPage] = useState(1)

  const columns: TableProps['columns'] = useMemo(
    () => [
      {
        id: 'status',
        Header: () => (
          <Gap className='align-start' space={4} vertical>
            <div className='text-semibold'>{t(ROOM_STATUS)}</div>
            <DropDownMenu
              value={filter.status}
              options={ROOM_STATUS_OPTIONS}
              onChange={onFilterChange('status')}
            />
          </Gap>
        ),
        accessor: ({ isAvailable, RoomPhotos }: RentAPerfect.Room) => (
          <Gap className='align-center' space={12}>
            <DotStatus $isAvailable={isAvailable} />
            <Image
              className='radius-8'
              src={getFileUrl(RoomPhotos?.[0]?.url)}
              width={100}
              height={60}
              cover
            />
          </Gap>
        ),
      },
      {
        id: 'bedroom',
        Header: () => (
          <Gap className='align-start' space={4} vertical>
            <div className='text-semibold'>{t(ROOM_TYPE)}</div>
            <DropDownMenu
              value={filter.bedroom}
              options={ROOM_TYPE_OPTIONS}
              onChange={onFilterChange('bedroom')}
            />
          </Gap>
        ),
        accessor: ({ RoomInfo }: RentAPerfect.Room) => (
          <div className='text-medium'>
            {RoomInfo ? t(N_BEDROOMS, { n: RoomInfo.roomNumber }) : '-'}
          </div>
        ),
      },
      {
        id: 'size',
        Header: () => (
          <Gap
            className='align-start width-fit-content mx-auto'
            space={4}
            vertical
          >
            <div className='text-semibold'>{t(ROOM_SIZE)}</div>
            <DropDownRange
              value={filter.size}
              onChange={onFilterChange('size')}
              min={SIZE_MIN}
              max={SIZE_MAX}
            />
          </Gap>
        ),
        accessor: ({ RoomInfo }: RentAPerfect.Room) => (
          <div className='text-center text-medium'>
            {RoomInfo ? numberToString(RoomInfo.area, { decimals: 2 }) : '-'}
          </div>
        ),
      },
      {
        id: 'floor',
        Header: () => (
          <Gap
            className='align-start width-fit-content mx-auto'
            space={4}
            vertical
          >
            <div className='text-semibold'>{t(FLOOR)}</div>
            <DropDownMenu
              value={filter.floor}
              options={FLOOR_OPTIONS}
              onChange={onFilterChange('floor')}
            />
          </Gap>
        ),
        accessor: ({ RoomInfo }: RentAPerfect.Room) => (
          <div className='text-center text-medium'>
            {RoomInfo ? numberToString(RoomInfo.floor) : '-'}
          </div>
        ),
      },
      {
        id: 'price',
        Header: () => (
          <Gap
            className='align-start width-fit-content mx-auto'
            space={4}
            vertical
          >
            <div className='text-semibold'>{t(RENTAL_PRICE_WITH_UNIT)}</div>
            <DropDownRange
              value={filter.price}
              onChange={onFilterChange('price')}
              prefixLabel='฿'
              min={PRICE_MIN}
              max={PRICE_MAX}
              step={PRICE_STEP}
            />
          </Gap>
        ),
        accessor: ({ RoomContracts }: RentAPerfect.Room) => (
          <div className='text-center text-medium'>
            {RoomContracts ? `฿${numberToString(RoomContracts.price)}` : '-'}
          </div>
        ),
      },
      {
        id: 'furniture',
        Header: () => (
          <Gap
            className='align-start width-fit-content mx-auto'
            space={4}
            vertical
          >
            <div className='text-semibold'>{t(FURNITURE)}</div>
            <DropDownMenu
              value={filter.furniture}
              options={FURNITURE_OPTIONS}
              onChange={onFilterChange('furniture')}
            />
          </Gap>
        ),
        accessor: (room: RentAPerfect.Room) => (
          <div className='text-center text-medium'>
            {getFurnitureText({ t, room })}
          </div>
        ),
      },
      {
        id: 'link',
        Header: '',
        style: { width: 0 },
        accessor: ({ id }: RentAPerfect.Room) => (
          <RoomDetailsButton to={resolvePath(ROOM_DETAIL_PATH, { roomId: id })}>
            {t(SEE_DETAILS)}
          </RoomDetailsButton>
        ),
      },
    ],
    [t, filter, onFilterChange]
  )

  return (
    <>
      <Table
        {...props}
        data={getPageData(data as RentAPerfect.Room[], page, PAGE_SIZE)}
        columns={columns}
      />
      {data.length ? (
        <Pagination
          className='mt-32 ml-auto'
          current={page}
          total={data.length}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      ) : null}
    </>
  )
}

export default UnitListTable
